import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SubmitButton } from '../../../components/mui/Buttons/Buttons';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { addProspectLead, getProspectLeadsTable } from '../../../redux/features/globalSlice';
import Loader3D from '../../../components/Loader/Loader3D';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { prospect_leads_departments } from '../../../utils/constants/departmentDetails';
import { project } from '../../../utils/constants/project';

const ProspectLeadsBulkUpload = ({ tableState, params, refreshTable }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((store) => store.global);

  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [excellConvertionLoading, setExcellConvertionLoading] = useState(false);

  const syllabusList = useSelector((state) => state.deo.syllabuses);
  const classList = useSelector((state) => state.deo.classes);

  const initialValues = {
    excelData: [],
  };

  const allowedKeys = [
    'name',
    'email',
    'country_code',
    'phone',
    'current_department_id',
    'alternative_country_code',
    'alternative_phone',
    'class_id',
    'syllabus_id',
    'parentName',
    'place',
    'project',
  ];

  const schema = Yup.object({
    excelData: Yup.array()
      .of(
        Yup.object()
          .shape({
            name: Yup.string().trim().required('Name is required'),
            email: Yup.string()
              .transform((value) => (value === '' ? null : value))
              .email('Invalid email')
              .nullable()
              .notRequired(),
            parentName: Yup.string().nullable(),
            place: Yup.string().nullable(),

            country_code: Yup.number().typeError('Country code must be numeric').required('Country code is required'),
            phone: Yup.number().typeError('Phone must be numeric').required('Phone is required'),

            alternative_country_code: Yup.number().typeError('Country code must be numeric'),
            alternative_phone: Yup.number().typeError('Phone must be numeric'),

            current_department_id: Yup.string().oneOf(['CSD', 'OGD'], 'Invalid Department'),
            project: Yup.string()
              .oneOf(
                [
                  'Little Genie',
                  'Classroom',
                  'Foundation',
                  'Speak Pro',
                  'STC',
                  'Personal Mentor',
                  'NA',
                  'Open School',
                  'Home School',
                  'Marketing',
                  'Other',
                ],
                'Invalid Project',
              )
              .nullable(),

            class_id: Yup.string()
              .oneOf(
                [
                  'LKG',
                  'UKG',
                  '1',
                  '2',
                  '3',
                  '4',
                  '5',
                  '6',
                  '7',
                  '8',
                  '9',
                  '10',
                  '11',
                  '12',
                  'Diploma Classes',
                  'UG Classes',
                  'PG Classes',
                  'Others',
                ],
                'Invalid Department',
              )
              .nullable(),
            syllabus_id: Yup.string()
              .oneOf(
                [
                  'State Board',
                  'CBSE',
                  'ICSE',
                  'CISCE',
                  'IGCSE',
                  'Others',
                  'CBSE - NCERT',
                  'CBSE - Other Publications',
                ],
                'Invalid Department',
              )
              .nullable(),
          })
          .test('no-extra-keys', 'Invalid keys found in Excel', (value) => {
            if (!value) return false;
            const extraKeys = Object.keys(value).filter((k) => !allowedKeys.includes(k));
            return extraKeys.length === 0;
          }),
      )
      .min(1, 'Please upload at least one row')
      .required('Excel data is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const data = values.excelData.map((item) => ({
        ...item,
        email: item.email ? item.email : null,
        current_department_id:
          prospect_leads_departments.find((p) => p?.name === item.current_department_id)?.id || null,
        // project: project.find((p) => p?.label === item.project)?.id || null,
        project: item.project || null,

        class_id:
          classList.find((c) => String(c.className).trim().toLowerCase() === String(item.class_id).trim().toLowerCase())
            ?.id || null,
        // Map Syllabus Name to ID
        syllabus_id:
          syllabusList.find(
            (s) => String(s.name).trim().toLowerCase() === String(item.syllabus_id).trim().toLowerCase(),
          )?.id || null,
        // Ensure alternative numbers are handled
        alternative_country_code: item.alternative_country_code || null,
        alternative_phone: item.alternative_phone || null,

        lead_father_name: item.parentName,
        lead_place: item.place,
      }));
      if (data.length > 500) {
        return enqueueSnackbar('Maximum 500 records allowed per upload.', { variant: 'error' });
      }
      dispatch(addProspectLead(data)).then((res) => {
        if (res.payload?.status === 'success') {
          refreshTable(); //refresh parent tablel
          handleCancel();
          enqueueSnackbar(res.payload.message, { variant: 'success' });
        } else {
          enqueueSnackbar(res.payload.message || 'Upload failed', { variant: 'error', autoHideDuration: 6000 });
          console.log('error', res.payload);
        }
      });

      // dispatch(getProspectLeadsTable(params));
    },
  });
  const downloadSampleExcel = () => {
    const link = document.createElement('a');
    link.href = '/sheets/prospectedLeads_v2.xlsx';
    link.download = 'prospected_leads_sheet.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
    // if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setExcellConvertionLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);
        formik.setFieldValue('excelData', json);
        formik.setFieldTouched('excelData', true);
      } catch (err) {
        formik.setFieldValue('excelData', []);
      } finally {
        setExcellConvertionLoading(false);
      }
    };

    reader.onerror = () => {
      setExcellConvertionLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  // ✅ FIXED Download Function (Works in all browsers)

  return (
    <GlobalDialog
      maxWidth="sm"
      fullWidth
      title="Bulk Upload"
      open={open}
      setOpen={setOpen}
      closeButton
      closeCondition={handleCancel}
      popupButton={
        <AddButton
          size="medium"
          fullWidth
          sx={{
            minWidth: 140,
            height: 40,
          }}
          action={() => setOpen(true)}
        >
          Bulk Upload
        </AddButton>
      }
      content={
        open ? (
          loading ? (
            <Loader3D />
          ) : (
            <>
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Box>
                    <Stack gap={2}>
                      {/* Download Button */}
                      <Box mb={1}>
                        <Button variant="outlined" size="small" onClick={downloadSampleExcel}>
                          <Icon icon="vscode-icons:file-type-excel2" width="22" />
                          <Typography ml={1} variant="button">
                            Download Excel Structure
                          </Typography>
                        </Button>
                      </Box>

                      {/* File Upload */}
                      <Box>
                        <TextField
                          name="excelData"
                          fullWidth
                          type="file"
                          inputRef={fileInputRef}
                          placeholder="Select an Excel File to Upload"
                          label="Select Excel File to upload"
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          onChange={handleFileChange}
                          accept=".xlsx, .xls"
                          error={formik.touched.excelData && Boolean(formik.errors.excelData)}
                          helperText={
                            formik.touched.excelData && typeof formik.errors.excelData
                              ? typeof formik.errors.excelData === 'string'
                                ? formik.errors.excelData
                                : Array.isArray(formik.errors.excelData) &&
                                    typeof formik.errors.excelData[0] === 'string'
                                  ? formik.errors.excelData[0]
                                  : 'Invalid or missing data in Excel rows.'
                              : ''
                          }
                        />
                      </Box>
                      {formik.errors.excelData && Array.isArray(formik.errors.excelData) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {typeof formik.errors.excelData[0] === 'string'
                            ? formik.errors.excelData
                            : formik.errors.excelData
                                .map((err, i) => (err ? `Row ${i + 1}: ${Object.values(err).join(', ')}` : ''))
                                .join(' | ')}
                        </Alert>
                      )}
                    </Stack>
                  </Box>

                  {/* Buttons */}
                  <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1}>
                      <CancelButton action={handleCancel}>Cancel</CancelButton>
                      <SubmitButton disabled={loading || excellConvertionLoading} type="submit">
                        {loading ? 'Processing...' : 'Submit'}
                      </SubmitButton>
                    </Stack>
                  </Box>
                </Form>
              </FormikProvider>
            </>
          )
        ) : null
      }
    />
  );
};

export default ProspectLeadsBulkUpload;
